import { createContext, useState, useMemo, useRef, } from 'react';
import { useId } from '@react-aria/utils';
import { useOverlayTriggerState, } from '@react-stately/overlays';
import { useOverlayTrigger, } from '@react-aria/overlays';
import { useField } from '@react-aria/label';
import useMergedRef from '@react-hook/merged-ref';
import { compact } from '../../utils/index.js';
import { findItemsByKeys, flattenItems, sortItemsAlphanumerically, } from './utils.js';
import { useSelectListState } from './use-select-list-state.js';
export const DEFAULT_PROPS = {
    variant: 'secondary',
    dataAutomation: 'select',
    selectionMode: 'single',
    emptyState: { heading: '', content: '' },
    fullWidth: false,
    isInModal: false,
    sortMode: 'chronological',
};
export const SelectContext = createContext({
    selectState: {},
});
export const useSelectState = (_props, forwardedRef) => {
    const props = { ...DEFAULT_PROPS, ..._props };
    const listId = useId();
    const controlLabelId = useId();
    const controlId = useId(props.id);
    const [uncontrolledFilterValue, setUncontrolledFilterValue] = useState('');
    const [filterActiveDescendant, setFilterActiveDescendant] = useState();
    const [cachedSelectedItems, setCachedSelectedItems] = useState(() => compact(props.selectionMode === 'multiple'
        ? props.selectedItems || props.defaultSelectedItems
        : [props.selectedItem || props.defaultSelectedItem]));
    const cachedSelectedIds = useMemo(() => cachedSelectedItems.map((obj) => obj.id), [cachedSelectedItems]);
    const flattenedItems = useMemo(() => flattenItems(props.defaultItems), [props.defaultItems]);
    const buttonRef = useRef(null);
    const buttonState = useOverlayTriggerState({});
    const overlayTriggerAria = useOverlayTrigger({ type: 'listbox' }, buttonState, buttonRef);
    const fieldAria = useField({
        ...props,
        description: typeof props.description === 'function'
            ? props.description()
            : props.description,
        id: controlId,
        'aria-labelledby': props.label ? undefined : props['aria-labelledby'],
    });
    const popoverProps = {
        isDismissable: true,
        shouldCloseOnBlur: true,
        autoFocus: true,
        restoreFocus: true,
    };
    const ref = useMergedRef(buttonRef, forwardedRef);
    const state = {
        listId,
        controlLabelId,
        controlId,
        uncontrolledFilterValue,
        setUncontrolledFilterValue,
        filterActiveDescendant,
        setFilterActiveDescendant,
        cachedSelectedItems,
        setCachedSelectedItems,
        cachedSelectedIds,
        flattenedItems,
        buttonRef,
        buttonState,
        overlayTriggerAria,
        fieldAria,
        popoverProps,
        ref,
    };
    const listState = useSelectListState(props, state);
    const { selectionManager } = listState;
    const { selectedKeys } = selectionManager;
    const selectedItems = useMemo(() => findItemsByKeys(flattenedItems, cachedSelectedItems)(selectedKeys), [listState.selectionManager.selectedKeys, cachedSelectedItems]);
    const selectState = {
        props,
        state: {
            ...state,
            listState,
            selectedItems: props.sortMode === 'alphanumeric'
                ? sortItemsAlphanumerically(selectedItems)
                : selectedItems,
        },
    };
    return selectState;
};
//# sourceMappingURL=use-select-state.js.map